import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../../components/common/Theme';

interface Option {
  label: string;
  value: string;
}

interface FilterableMultiSelectProps {
  label: string;
  options: Option[];
  selected: string[]; // this stores the selected GUIDs
  setSelected: (values: string[]) => void;
}

const FilterableMultiSelect: React.FC<FilterableMultiSelectProps> = ({
  label,
  options,
  selected,
  setSelected,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { theme } = useTheme();
  const isLight = theme === 'white';

  const toggleSelection = (value: string) => {
    if (selected.includes(value)) {
      setSelected(selected.filter(v => v !== value));
    } else {
      setSelected([...selected, value]);
    }
  };

  const filtered = options.filter(opt =>
    opt.label.toLowerCase().includes(search.toLowerCase())
  );

  const getLabel = (value: string) =>
    options.find(opt => opt.value === value)?.label || value;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative text-[11px]" ref={wrapperRef}>
      <label className={`block mb-1 ${isLight ? 'text-neutral-600' : 'text-gray-400'}`}>{label}</label>
      <div
        className={`px-3 py-2 rounded cursor-pointer border ${isLight ? 'bg-white text-neutral-900 border-neutral-300' : 'bg-neutral-800 text-white border-neutral-700'}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {selected.length > 0 ? selected.map(getLabel).join(', ') : 'Select...'}
      </div>
      {isOpen && (
        <div className={`absolute z-20 mt-1 w-full rounded shadow-lg max-h-48 overflow-y-auto border ${isLight ? 'bg-white border-neutral-200' : 'bg-neutral-900 border-neutral-700'}`}>
          <input
            type="text"
            placeholder="Search..."
            className={`w-full px-3 py-2 text-[11px] border-b outline-none ${isLight ? 'bg-neutral-50 text-neutral-900 border-neutral-200 placeholder-neutral-400' : 'bg-neutral-800 text-white border-neutral-700'}`}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div className="max-h-40 overflow-y-auto">
            {filtered.map(option => (
              <div
                key={option.value}
                className={`px-3 py-1.5 cursor-pointer text-[11px] ${
                  selected.includes(option.value)
                    ? 'bg-indigo-600 text-white'
                    : isLight
                      ? 'text-neutral-700 hover:bg-neutral-100'
                      : 'text-gray-300 hover:bg-neutral-700'
                }`}
                onClick={() => toggleSelection(option.value)}
              >
                {option.label}
              </div>
            ))}
            {filtered.length === 0 && (
              <div className={`px-3 py-2 text-[11px] ${isLight ? 'text-neutral-400' : 'text-gray-500'}`}>No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterableMultiSelect;
